import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { authApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack line-user-id-resolver
 * @graph-domain Engineering
 * @graph-business 認証フック。/auth/me でセッション検証・login（Edge Auth へのクライアントリダイレクト）/logout ミューテーションを提供
 * @graph-connects /auth/me [calls] 現在のユーザーセッション取得API
 * @graph-connects /auth/logout [calls] ログアウトAPI
 * @graph-connects authApi [calls] 認証APIクライアント経由でログアウト・セッション取得
 * @graph-connects buildEdgeAuthStartUrl [calls] login mutation で Edge Auth start URL に window.location でリダイレクト
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.auth.current,
    queryFn: authApi.getCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: () => {
      window.location.href = buildEdgeAuthStartUrl(window.location.href);
      return Promise.resolve();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.setQueryData(queryKeys.auth.current, null);
      window.location.href = '/login';
    },
  });

  const user = data?.user ?? null;

  return {
    user,
    expiresAt: data?.expiresAt,
    isAuthenticated: !!user,
    isLoading,
    error,
    refetch,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
  };
}
